import { db } from "helpers/firebase/firebaseConfig";
import { call, put, takeLatest, all } from "redux-saga/effects";
import { toast } from "react-toastify";
import {
  FETCH_ORDERS_REQUEST,
  fetchOrdersSuccess,
  fetchOrdersFailure,
  updateOrderStatusSuccess,
  UPDATE_ORDER_STATUS_REQUEST,
} from "./actions";
import { collection, getDocs,getDoc, updateDoc, doc, addDoc } from "firebase/firestore";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { recordProviderDelayIncident } from "../../helpers/api/cancellationHelper";

// Collection names
const ORDERS_COLLECTION = "Booking";
const WETB_NOTIFICATIONS_COLLECTION = "serviceProvidersNotifications";
const APP_NOTIFICATIONS_COLLECTION = "app_notifications";

const getBookingExecuteDate = (bookingExecuteDate) => {
  if (!bookingExecuteDate) return null;
  if (bookingExecuteDate?.seconds) {
    return new Date(bookingExecuteDate.seconds * 1000);
  }

  const parsed = new Date(bookingExecuteDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isOrderLiveDelayed = (orderData = {}) => {
  const bookingStatus = String(orderData?.booking_status || "").toLowerCase();
  const paymentStatus = String(orderData?.paymentStatus || "").toLowerCase();
  const executeDate = getBookingExecuteDate(orderData?.booking_execute_date);

  if (!executeDate) return false;

  const eligibleStatus = bookingStatus === "pending" || bookingStatus === "approved";
  const isPaid = paymentStatus === "paid";

  return eligibleStatus && isPaid && executeDate.getTime() < Date.now();
};

const hasDelayLock = (orderData = {}) => Boolean(orderData?.delay_locked);

// Fetch orders from Firebase
// Updated fetchOrdersFromAPI function in your saga
async function fetchOrdersFromAPI() {
  try {
    const ordersSnapshot = await getDocs(collection(db, ORDERS_COLLECTION));
    const orders = [];
    // Fetch all related data in parallel
    const [providersSnapshot, usersSnapshot, servicesSnapshot, addressSnapshot] = await Promise.all([
      getDocs(collection(db, "service_providers")),
      getDocs(collection(db, "app_users")),
      getDocs(collection(db, "service_provider_services")),
      getDocs(collection(db, "app_user_addresses"))
    ]);

    // Create maps for quick lookup
    const providersMap = new Map(providersSnapshot.docs.map(doc => [doc.id, doc.data()]));
    const usersMap = new Map(usersSnapshot.docs.map(doc => [doc.id, doc.data()]));
    const servicesMap = new Map(servicesSnapshot.docs.map(doc => [doc.id, doc.data()]));
    const user_addresses = new Map(addressSnapshot.docs.map(doc => [doc.id, doc.data()]));
    // Process each order
    for (const orderDoc of ordersSnapshot.docs) {
      const orderData = orderDoc.data();
      const shouldLockDelay = isOrderLiveDelayed(orderData) && !hasDelayLock(orderData);

      if (shouldLockDelay) {
        await updateDoc(doc(db, ORDERS_COLLECTION, orderDoc.id), {
          delay_locked: true,
          delay_locked_at: orderData.delay_locked_at || new Date().toISOString(),
        });
      }

      const enrichedOrder = {
        id: orderDoc.id,
        ...orderData,
        delay_locked: shouldLockDelay ? true : Boolean(orderData.delay_locked),
        delay_locked_at: orderData.delay_locked_at || (shouldLockDelay ? new Date().toISOString() : null),
        seen: orderData.seen ?? false, // ensure seen field exists
        service_providers_id: providersMap.get(orderData.service_providers_id) || null,
        app_user_id: usersMap.get(orderData.app_user_id) || null,
        serviceProviderServices: servicesMap.get(orderData.serviceProviderServices) || null,
        user_address_id: user_addresses.get(orderData.user_address_id ) || null,
      };
      orders.push(enrichedOrder);
    }

    return orders;
  } catch (error) {
    console.log("Error fetching orders:", error);
    throw error;
  }
}

// Update order status in Firebase
async function updateOrderStatusInAPI(orderId, newStatus) {
  try {
    const orderRef = doc(db, ORDERS_COLLECTION, orderId);
    const currentOrderSnap = await getDoc(orderRef);

    if (!currentOrderSnap.exists()) {
      throw new Error("Order not found");
    }

    const currentOrderData = currentOrderSnap.data();
    const shouldKeepDelayLock = hasDelayLock(currentOrderData) || isOrderLiveDelayed(currentOrderData);

    const updatePayload = {
      booking_status: newStatus,
      updatedAt: new Date().toISOString(),
    };

    if (shouldKeepDelayLock) {
      updatePayload.delay_locked = true;
      updatePayload.delay_locked_at = currentOrderData.delay_locked_at || new Date().toISOString();
    }

    await updateDoc(orderRef, updatePayload);
    
    // Get the updated order
    const orderSnap = await getDoc(orderRef); // Use getDoc for a single document
    return { id: orderSnap.id, ...orderSnap.data() };
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
}

async function getOrderById(orderId) {
  const orderRef = doc(db, ORDERS_COLLECTION, orderId);
  const orderSnap = await getDoc(orderRef);

  if (!orderSnap.exists()) {
    throw new Error("Order not found");
  }

  return { id: orderSnap.id, ...orderSnap.data() };
}

function* fetchOrders() {
  try {
    const orders = yield call(fetchOrdersFromAPI);

    yield put(fetchOrdersSuccess(orders));
  } catch (error) {
    yield put(fetchOrdersFailure(error));
  }
}

function* updateOrderStatus({ payload }) {
  try {
    const isProviderDelay = payload.newStatus === "provider_delay";

    // First update the order status
    const response = yield call(
      isProviderDelay ? getOrderById : updateOrderStatusInAPI,
      payload.orderId,
      payload.newStatus
    );

    if (response) {
      // Fetch all related data needed for notifications
      const [serviceSnapshot, userSnapshot, providerSnapshot] = yield all([
        call(getDoc, doc(db, "service_provider_services", response.serviceProviderServices)),
        call(getDoc, doc(db, "app_users", response.app_user_id)),
        call(getDoc, doc(db, "service_providers", response.service_providers_id))
      ]);

      // Get the data from snapshots with fallbacks
      const enrichedResponse = {
        ...response,
        serviceProviderServices: serviceSnapshot.exists() 
          ? { id: serviceSnapshot.id, ...serviceSnapshot.data() } 
          : { id: response.serviceProviderServices, name: "الخدمة" },
        app_user_id: userSnapshot.exists() 
          ? { id: userSnapshot.id, ...userSnapshot.data() } 
          : { id: response.app_user_id },
        service_providers_id: providerSnapshot.exists()
          ? { id: providerSnapshot.id, ...providerSnapshot.data() }
          : { id: response.service_providers_id }
      };

      if (isProviderDelay) {
        const delayResult = yield call(recordProviderDelayIncident, enrichedResponse);

        const providerNotificationData = {
          title: "تنبيه سياسة التأخير",
          content: delayResult.isWarningOnly
            ? `تم تسجيل أول حالة تأخير على طلب ${enrichedResponse.serviceProviderServices.name} كإنذار بدون غرامة.`
            : `تم تسجيل حالة تأخير متكررة على طلب ${enrichedResponse.serviceProviderServices.name} وإضافة غرامة 30 ر.س إلى التسوية القادمة.`,
          notification_type: "BOOKING",
          orderId: enrichedResponse.id,
          seen: false,
          serviceProviders: enrichedResponse.service_providers_id.id,
          created_at: new Date().toISOString(),
        };

        const notificationTokens = Array.isArray(enrichedResponse.service_providers_id.notification_token)
          ? enrichedResponse.service_providers_id.notification_token
          : [];

        for (const token of notificationTokens) {
          try {
            yield call(createNotificationInAPI, providerNotificationData, token);
          } catch (error) {
          }
        }

        yield put(updateOrderStatusSuccess(enrichedResponse));
        toast.success(delayResult.message);
        return;
      }

      // Prepare notification data with the fetched information
      const getType = (type) => {
        switch (type) {
          case "approved": return `تم الموافقة على الطلب`;
          case "pending": return "قيد الانتظار";
          case "rejected": return "مرفوض";
          case "progress": return "قيد التنفيذ";
          case "completed": return "مكتمل";
          case "cancelled_by_admin": return "ألغي بواسطة الإدارة";
          default: return "/";
        }
      };

      // App user notification
      const notificationDataForApp = {
        title: "تحديث حالة الحجز",
        content: `تم تحديث حالة الحجز ${enrichedResponse.serviceProviderServices.name} إلى ${getType(payload.newStatus)}`,
        notification_type: "BOOKING",
        orderId: enrichedResponse.id,
        isSeen: false,
        appUser: enrichedResponse.app_user_id.id,
        created_at: new Date().toISOString(),
      };

      try {
        yield call(appNotification, notificationDataForApp, enrichedResponse.app_user_id.notification_token);
      } catch (error) {
        // console.error("App notification error:", error);
      }

      // Service provider notification
      const notificationData = {
        title: "تم تحديث حالة الطلب",
        content: `تم تحديث حالة الطلب ${enrichedResponse.serviceProviderServices.name} الخاص بك إلى ${getType(payload.newStatus)} من قبل الإدارة.`,
        notification_type: "BOOKING",
        orderId: enrichedResponse.id,
        seen: false,
        serviceProviders: enrichedResponse.service_providers_id.id,
        created_at: new Date().toISOString(),
      };

      // Send notifications to all service provider tokens
      const notificationTokens = Array.isArray(enrichedResponse.service_providers_id.notification_token) 
        ? enrichedResponse.service_providers_id.notification_token 
        : [];

      for (const token of notificationTokens) {
        try {
          yield call(createNotificationInAPI, notificationData, token);
        } catch (error) {
          // console.error("Notification error:", error);
        }
      }

      // Dispatch success with enriched response containing all nested data
      yield put(updateOrderStatusSuccess(enrichedResponse));
    }
  } catch (error) {
    toast.error(error?.message || "تعذر تنفيذ الإجراء المطلوب");
  }
}

async function createNotificationInAPI(notificationData, deviceToken) {
  const API_URL = 'https://node-server-eta-coral.vercel.app/send-notification';
  
  try {
    // Send push notification if token exists
    if (deviceToken) {
      await axios.post(API_URL, {
        deviceToken,
        title: notificationData.title,
        body: notificationData.content
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_NODE_API_KEY
        }
      });
    }

    // Save notification to Firestore
    const docRef = await addDoc(collection(db, WETB_NOTIFICATIONS_COLLECTION), {
      ...notificationData,

    });

    return docRef.id;
  } catch (error) {
    // console.error('Notification creation failed:', error);
    throw error;
  }
}

async function appNotification(notificationData, deviceToken) {
  const API_URL = 'https://node-server-eta-coral.vercel.app/send-notification';
  const title = notificationData.title;
  const body = notificationData.content;

  try {
    // Send push notification
    if (deviceToken) {
      try {
        await axios.post(API_URL, {
          deviceToken,
          title,
          body
        }, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_NODE_API_KEY
          }
        });
      } catch (err) {
        if (err.response?.status === 410) {
          console.warn('Invalid FCM token, removing from DB');

          // 🔥 Remove token from Firestore
          await updateDoc(doc(db, 'users', userId), {
            deviceToken: null
          });
        } else {
          // console.error('Notification error:', err.message);
        }
      }
    }
  } catch (error) {
    // console.error('App notification error:', error);
    throw error;
  }
}

function* watchFetchOrders() {
  yield takeLatest(FETCH_ORDERS_REQUEST, fetchOrders);
  yield takeLatest(UPDATE_ORDER_STATUS_REQUEST, updateOrderStatus);
}

export default watchFetchOrders;